import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { BookUser, Loader2, Search, Utensils } from 'lucide-react'
import { apiClient } from '@/api/client'
import { cn } from '@/lib/utils'
import {
  chargeErrorEffect,
  isValidManagerPin,
  sanitizeManagerPin,
  type ChargeAccountServerError,
} from '@/lib/houseAccounts'
import type { HouseAccount, HouseAccountKind } from '@/types'

/**
 * F-MM-N30 — charge the whole bill to a house account.
 *
 *   • Staff meal: no tax, no service charge, not revenue. Stock still comes
 *     off. A manager PIN is required on every one; going over the monthly
 *     allowance is allowed and logged against the meal.
 *   • Tab: a normal taxed sale, filed today, billed at the account's rate and
 *     paid later. Over the credit limit needs a manager PIN.
 *
 * The amount shown here is indicative only: the server reprices the bill at
 * the account's terms and the receipt prints the server's figures.
 */

export interface ChargeToAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Kinds this tenant has switched on. */
  staffEnabled: boolean
  tabsEnabled: boolean
  /** Current bill total at the selected tender — used for the limit hint only. */
  billTotal: number
  /** Order's attached guest, preselected when they hold an account. */
  customerId?: string | null
  formatCurrency: (amount: number) => string
  onConfirm: (account: HouseAccount, managerPin: string | undefined) => void
  pending?: boolean
  /**
   * The server's refusal of the last charge. The server decides whether a
   * PIN is needed (its balance and rate are authoritative), so a
   * limit / PIN refusal always opens the PIN field here.
   */
  serverError?: ChargeAccountServerError | null
}

export function ChargeToAccountDialog({
  open,
  onOpenChange,
  staffEnabled,
  tabsEnabled,
  billTotal,
  customerId,
  formatCurrency,
  onConfirm,
  pending,
  serverError,
}: ChargeToAccountDialogProps) {
  const [kind, setKind] = useState<HouseAccountKind>(staffEnabled ? 'staff_allowance' : 'tab')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selected, setSelected] = useState<HouseAccount | null>(null)
  const [pin, setPin] = useState('')
  const [forcePin, setForcePin] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [wrongPin, setWrongPin] = useState(false)

  const resetServerState = () => {
    setForcePin(false)
    setNotice(null)
    setWrongPin(false)
  }

  // Reset on every open: an account left selected from the previous bill is
  // an account somebody eventually charges by accident.
  useEffect(() => {
    if (!open) return
    setKind(staffEnabled ? 'staff_allowance' : 'tab')
    setQuery('')
    setDebounced('')
    setSelected(null)
    setPin('')
    resetServerState()
  }, [open, staffEnabled])

  useEffect(() => {
    if (!open || !serverError) return
    const fx = chargeErrorEffect(serverError.code)
    if (fx.forcePin) setForcePin(true)
    if (fx.clearPin) setPin('')
    setWrongPin(fx.wrongPin)
    setNotice(fx.wrongPin ? null : serverError.message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverError?.at])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const { data, isFetching } = useQuery({
    queryKey: ['counter-house-accounts', kind, debounced],
    queryFn: () => apiClient.searchCounterHouseAccounts({ q: debounced || undefined, kind }),
    enabled: open,
    staleTime: 15_000,
  })
  const accounts: HouseAccount[] = data?.data ?? []

  // Preselect the bill's own guest when they hold an account of this kind.
  useEffect(() => {
    if (!open || selected || !customerId) return
    const own = accounts.find((a) => a.customer_id === customerId)
    if (own) setSelected(own)
  }, [open, accounts, customerId, selected])

  const isStaff = selected?.kind === 'staff_allowance'
  const overLimit =
    selected?.kind === 'tab' &&
    typeof selected.credit_available === 'number' &&
    billTotal > selected.credit_available
  const pinRequired = isStaff || overLimit || forcePin
  const pinOk = !pinRequired || isValidManagerPin(pin)
  const canConfirm = Boolean(selected) && pinOk && !pending

  const confirm = () => {
    if (!selected || !canConfirm) return
    setNotice(null)
    setWrongPin(false)
    onConfirm(selected, pin || undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* [&>*]:min-w-0 — DialogContent is a grid, and a grid item otherwise
          grows to its longest unbreakable line: a long guest name pushed the
          amounts and the confirm button out of the dialog. */}
      <DialogContent className="sm:max-w-md [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookUser className="h-4 w-4" aria-hidden />
            Charge to account
          </DialogTitle>
          <DialogDescription>
            The whole bill goes on the account. Nothing is collected now.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {staffEnabled && tabsEnabled ? (
            <div className="grid grid-cols-2 gap-1.5" role="tablist">
              {(
                [
                  ['staff_allowance', 'Staff meal', Utensils],
                  ['tab', 'Customer tab', BookUser],
                ] as const
              ).map(([k, label, Icon]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={kind === k}
                  onClick={() => {
                    setKind(k)
                    setSelected(null)
                    setPin('')
                    resetServerState()
                  }}
                  className={cn(
                    'flex h-10 items-center justify-center gap-1.5 rounded-lg border text-sm font-medium transition-colors',
                    kind === k
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background hover:bg-muted/50',
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              autoFocus
              placeholder="Name or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 pl-9"
            />
          </div>

          <div className="max-h-56 space-y-1 overflow-y-auto">
            {isFetching && accounts.length === 0 ? (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Looking up accounts…
              </div>
            ) : accounts.length === 0 ? (
              <p className="py-3 text-sm text-muted-foreground">
                {kind === 'tab'
                  ? 'No active tabs match. An admin or manager opens one on the guest’s profile in Customers.'
                  : 'Nobody on a meal allowance matches. An admin gives one in Manage Staff.'}
              </p>
            ) : (
              accounts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setSelected(a)
                    setPin('')
                    resetServerState()
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    selected?.id === a.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium" title={a.person_name ?? a.customer_name}>
                      {a.person_name ?? a.customer_name}
                    </span>
                    {a.customer_phone ? (
                      <span className="block truncate text-xs text-muted-foreground">{a.customer_phone}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {a.kind === 'tab' ? (
                      <>Owes {formatCurrency(a.balance)}</>
                    ) : (
                      <>{formatCurrency(a.allowance_remaining)} left</>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>

          {selected ? (
            <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-3 text-sm">
              {isStaff ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Allowance left this month</span>
                    <span className="font-medium tabular-nums">{formatCurrency(selected.allowance_remaining)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Charged at menu value — no tax, no service charge. Anything above the allowance is logged as an
                    overage against this meal.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Owes now</span>
                    <span className="font-medium tabular-nums">{formatCurrency(selected.balance)}</span>
                  </div>
                  {typeof selected.credit_available === 'number' ? (
                    selected.credit_available < 0 ? (
                      // Already past the limit: a negative "credit available" read as a typo.
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Over the limit by</span>
                        <span className="font-medium tabular-nums text-amber-700 dark:text-amber-400">
                          {formatCurrency(-selected.credit_available)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Credit available</span>
                        <span className="font-medium tabular-nums">{formatCurrency(selected.credit_available)}</span>
                      </div>
                    )
                  ) : null}
                  <p className="text-[11px] text-muted-foreground">
                    Billed at the {selected.tab_rate_intent === 'card' ? 'card' : 'cash'} tax rate and filed today.
                    {selected.tab_rate_intent === 'card' ? ' It can only be paid off by card or online.' : ''}
                  </p>
                  {overLimit && !notice ? (
                    <p role="alert" className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      This bill takes the tab over its credit limit — a manager PIN is needed.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {notice ? (
            <p role="alert" className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {notice}
            </p>
          ) : null}

          {selected && pinRequired ? (
            <div className="space-y-1.5">
              <label htmlFor="house-account-pin" className="text-sm font-medium">
                Manager PIN
              </label>
              <Input
                id="house-account-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="••••"
                value={pin}
                aria-invalid={wrongPin || undefined}
                onChange={(e) => {
                  setPin(sanitizeManagerPin(e.target.value))
                  setWrongPin(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canConfirm) {
                    e.preventDefault()
                    confirm()
                  }
                }}
                className="h-10 tracking-widest"
              />
              {wrongPin ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  Wrong PIN
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canConfirm} onClick={confirm}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            {(selected?.kind ?? kind) === 'staff_allowance' ? 'Charge staff meal' : 'Charge to tab'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
